/** Session + preferences + tile plan, wired together for the call room. */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { CallCredentials } from '@cyphex/types/calls';
import type { MediaClient, MediaParticipant } from '@/features/meeting/media/contract';
import { ceilingForRole } from '@/features/meeting/media/densityPolicy';
import type { MeetingLayout, TilePlan, TileView } from '@/features/meeting/media/tilePlan';
import { useMediaPreferences, type MediaPreferencesApi } from '@/features/meeting/hooks/useMediaPreferences';
import { useTilePlan } from '@/features/meeting/hooks/useTilePlan';
import { useCallSession, type CallSession } from './useCallSession';

export interface CallRoom {
  readonly session: CallSession;
  readonly media: MediaPreferencesApi;
  readonly plan: TilePlan;
  readonly layout: MeetingLayout;
  readonly shareTile: TileView | null;
  readonly local: MediaParticipant | undefined;
  readonly client: MediaClient;
  readonly stageRef: React.RefObject<HTMLDivElement>;
  readonly pinnedId: string | null;
  /**
   * Which page of tiles is on screen.
   *
   * `page: 0` was hardcoded here and `CallStage` had no pager at all, so a call
   * was capped at `MAX_VISIBLE_TILES` faces with no way to reach anybody past
   * them. Pagination is not a nicety in this architecture — `buildTilePlan`
   * unsubscribes every publisher that is not on the visible page, so people on
   * page 2 were not merely off screen, their video was not being forwarded at
   * all and nothing on screen said they existed.
   *
   * Read back from the PLAN rather than from the state, because
   * `buildTilePlan` clamps it: somebody leaving a call can take the last page
   * away underneath a viewer who is standing on it.
   */
  readonly page: number;
  setPinned(participantId: string | null): void;
  setPage(page: number): void;
}

export function useCallRoom(
  callId: string,
  credentials: CallCredentials,
  withVideo: boolean,
): CallRoom {
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  /* The session needs the preferences to connect, so the client is handed back
     by ref once it exists — the same contract the meeting room uses. */
  const clientRef = useRef<MediaClient | null>(null);
  const media = useMediaPreferences(clientRef);

  const session = useCallSession({
    callId,
    credentials,
    camera: withVideo,
    mic: true,
    preferences: media.preferences,
  });
  clientRef.current = session.client;

  const { snapshot, client } = session;
  /* A share takes over the room: two people looking at a shared screen do not
     want it as one of four equal squares. */
  const layout: MeetingLayout = snapshot.screenShareOwnerId === null ? 'gallery' : 'presentation';

  const { plan, stageRef } = useTilePlan({
    client,
    participants: snapshot.participants,
    activeSpeakerIds: snapshot.activeSpeakerIds,
    localId: snapshot.localId,
    screenShareOwnerId: snapshot.screenShareOwnerId,
    pinnedId,
    layout,
    profile: snapshot.profile,
    page,
    videoSuspended: false,
    showSelfTile: true,
  });

  const local = useMemo(
    () => snapshot.participants.find((participant) => participant.isLocal),
    [snapshot.participants],
  );

  const shareTile: TileView | null = useMemo(() => {
    const owner = snapshot.participants.find((p) => p.id === snapshot.screenShareOwnerId);
    if (!owner) return null;
    return { participant: owner, role: 'screen', layer: ceilingForRole('screen', snapshot.profile) };
  }, [snapshot.participants, snapshot.screenShareOwnerId, snapshot.profile]);

  const setPinned = useCallback((participantId: string | null) => setPinnedId(participantId), []);
  const goToPage = useCallback((next: number) => setPage(Math.max(0, next)), []);

  return {
    session, media, plan, layout, shareTile, local, client, stageRef, pinnedId,
    page: plan.page, setPinned, setPage: goToPage,
  };
}
